import React from 'react';

// Contract status types matching backend
export type ContractStatus =
  | 'uploaded'
  | 'processing'
  | 'ocr_complete'
  | 'transferring_to_r2'
  | 'processed'
  | 'extracting'
  | 'extraction_complete'
  | 'extraction_failed'
  | 'structured'
  | 'embedded'
  | 'linked'
  | 'ready'
  | 'error'
  | 'processing_failed';

// Status configuration using design system colors
// Warning (orange) = incomplete/needs action, Danger (red) = failed, Success (green) = complete
export const statusConfig: Record<ContractStatus, { label: string; color: string; bgColor: string }> = {
  uploaded: { label: 'Uploaded', color: 'text-neutral-700', bgColor: 'bg-neutral-100' },
  processing: { label: 'Processing (Stuck)', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  ocr_complete: { label: 'Transfer Pending', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  transferring_to_r2: { label: 'Transferring (Stuck)', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  processed: { label: 'Needs Extraction', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  extracting: { label: 'Extracting (Stuck)', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  extraction_complete: { label: 'Needs Follow-up', color: 'text-warning-700', bgColor: 'bg-warning-100' },
  extraction_failed: { label: 'Extraction Failed', color: 'text-danger-700', bgColor: 'bg-danger-100' },
  structured: { label: 'Structured', color: 'text-secondary-700', bgColor: 'bg-secondary-100' },
  embedded: { label: 'Embedded', color: 'text-success-700', bgColor: 'bg-success-100' },
  linked: { label: 'Linked', color: 'text-success-700', bgColor: 'bg-success-100' },
  ready: { label: 'Ready', color: 'text-success-700', bgColor: 'bg-success-100' },
  error: { label: 'Error', color: 'text-danger-700', bgColor: 'bg-danger-100' },
  processing_failed: { label: 'Processing Failed', color: 'text-danger-700', bgColor: 'bg-danger-100' },
};

// Interrupted/incomplete states that can be resumed
// Includes both stuck intermediate states and explicitly failed states
export const INTERRUPTED_STATES: ContractStatus[] = [
  'processing',           // Stuck during OCR
  'ocr_complete',         // OCR done, waiting for transfer
  'transferring_to_r2',   // Stuck during transfer
  'processed',            // Needs structure extraction
  'extracting',           // Stuck during extraction
  'extraction_complete',  // Extraction done, needs follow-up
  'extraction_failed',    // Extraction failed
  'processing_failed',    // General processing failure
];

export const StatusBadge: React.FC<{ status: ContractStatus }> = ({ status }) => {
  const config = statusConfig[status] || statusConfig.uploaded;
  return React.createElement(
    'span',
    {
      className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`,
      'data-testid': `status-badge-${status}`,
    },
    config.label
  );
};
